import { Assets, Container, Sprite, type Texture } from 'pixi.js';
import type { Vec2 } from '@nanocars/shared';
import { EFFECT_DUST_PATHS, EFFECT_SPLASH_PATH } from '../assets/manifest';

const DUST_LIFETIME_S = 0.45;
const SPLASH_LIFETIME_S = 0.6;
const DUST_SIZE_M = 0.55;
const SPLASH_SIZE_M = 1.1;
const DUST_DRIFT_MPS = 0.8;
const DUST_GROWTH_PER_S = 0.9;

// Per-car rate limit so a sustained drift or a car resting against a wall
// spawns a steady trickle of puffs instead of one every single frame.
const DUST_COOLDOWN_S = 0.08;

interface Particle {
  sprite: Sprite;
  age: number;
  lifetime: number;
  vel: Vec2;
  growth: number;
}

// Short-lived world-space particles: dust puffs (drifting on wood or
// scraping a wall) and the splash sprite shown when a car goes off the
// table into the falloff. Purely visual/decorative - RaceScene decides
// *when* to trigger these from car state; this class just owns the pool.
export class Effects {
  readonly layer = new Container();

  private dustTextures: Texture[] = [];
  private splashTexture: Texture | null = null;
  private readonly particles: Particle[] = [];
  private readonly lastDustAt = new Map<number, number>();
  private clock = 0;

  async build(): Promise<void> {
    this.dustTextures = await Promise.all(Object.values(EFFECT_DUST_PATHS).map((p) => Assets.load<Texture>(p)));
    this.splashTexture = await Assets.load<Texture>(EFFECT_SPLASH_PATH);
  }

  update(dt: number): void {
    this.clock += dt;
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.age += dt;
      p.sprite.x += p.vel.x * dt;
      p.sprite.y += p.vel.y * dt;
      p.sprite.scale.set(p.sprite.scale.x + p.growth * dt);
      p.sprite.alpha = Math.max(0, 1 - p.age / p.lifetime);
      if (p.age >= p.lifetime) {
        this.layer.removeChild(p.sprite);
        this.particles.splice(i, 1);
      }
    }
  }

  // Spawns a dust puff behind carId, at world position pos, facing heading
  // (radians). Rate-limited per car via DUST_COOLDOWN_S.
  maybeDust(carId: number, pos: Vec2, heading: number): void {
    if (this.dustTextures.length === 0) return;
    const last = this.lastDustAt.get(carId) ?? -Infinity;
    if (this.clock - last < DUST_COOLDOWN_S) return;
    this.lastDustAt.set(carId, this.clock);

    const texture = this.dustTextures[Math.floor(Math.random() * this.dustTextures.length)]!;
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.width = DUST_SIZE_M;
    sprite.height = DUST_SIZE_M;
    sprite.rotation = Math.random() * Math.PI * 2;
    sprite.position.set(pos.x, pos.y);
    this.layer.addChild(sprite);

    const behind = heading + Math.PI + (Math.random() - 0.5) * 0.6;
    this.particles.push({
      sprite,
      age: 0,
      lifetime: DUST_LIFETIME_S,
      vel: { x: Math.cos(behind) * DUST_DRIFT_MPS, y: Math.sin(behind) * DUST_DRIFT_MPS },
      growth: DUST_GROWTH_PER_S,
    });
  }

  // Drops every live particle and the layer itself. The dust/splash
  // Textures come from Assets' cache and are shared, so they are not
  // destroyed here.
  destroy(): void {
    this.particles.length = 0;
    this.lastDustAt.clear();
    this.layer.destroy({ children: true });
  }

  splash(pos: Vec2): void {
    if (!this.splashTexture) return;
    const sprite = new Sprite(this.splashTexture);
    sprite.anchor.set(0.5);
    sprite.width = SPLASH_SIZE_M;
    sprite.height = SPLASH_SIZE_M;
    sprite.position.set(pos.x, pos.y);
    this.layer.addChild(sprite);
    this.particles.push({ sprite, age: 0, lifetime: SPLASH_LIFETIME_S, vel: { x: 0, y: 0 }, growth: 0.3 });
  }
}
